import { FC } from 'react';
import Label from '@dealroadshow/uikit-input/Label';
import DateTimePicker from '@dealroadshow/uikit-picker/DateTimePicker';
import { Field, useForm } from '@framework/ui/Organisms/FinalForm';

import { Radio, RadioGroup } from '@dealroadshow/uikit-input/Radio';
import { FormStateSpecification } from '@dealroadshow/uikit-form';
import Tooltip from '@dealroadshow/uikit-tooltip';
import IconQuestion from '@dealroadshow/uikit-icon/IconQuestion';

import format from 'date-fns-tz/format';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import getQuestionTextValue from './helpers/getQuestionTextValue';

import { QUESTIONS_OACC } from './constants';
import { MONTH_DAY_YEAR_TIME_TIMEZONE_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import { TRoadshowSharedPresentationFormValues } from './interfaces';
import { IFinalFormFieldInputComponentProps } from '@framework/ui/Organisms/FinalForm/interfaces';
import { useCallFormContext } from './CallFormContext';

import styles from './callForm.module.scss';
import questionFieldsStyles from './QuestionFields/questionFields.module.scss';

interface IProps {
  isCallExpired?: boolean,
}

interface IRoadshowDateTimePicker extends IFinalFormFieldInputComponentProps<number | null> {
  timeZone: string | null,
  label: string,
  inputDataTest: string,
  disabled: boolean,
  minDate?: Date,
  minTime?: Date,
  placeholderText: string,
  dataTest: string,
  htmlFor: string,
}

const RoadshowDateTimePicker = ({
  meta,
  input,
  timeZone,
  label,
  inputDataTest,
  htmlFor,
  disabled,
  minDate,
  minTime,
  placeholderText,
  dataTest,
}: IRoadshowDateTimePicker) => (
  <>
    <Label htmlFor={ htmlFor }>
      { label }
    </Label>
    <DateTimePicker
      value={ input.value ? getDateFromUnixTime(input.value) : null }
      onChange={ (value: Date | null) => input.onChange(value ? getUnixTimestamp(value) : null) }
      isDisabled={ disabled }
      isNarrow
      isClearable={ false }
      meta={ meta }
      minDate={ minDate }
      minTime={ minTime }
      placeholder={ placeholderText }
      formatValue={ (date: Date) => format(date, MONTH_DAY_YEAR_TIME_TIMEZONE_FORMAT, { timeZone }) }
      dataTest={ dataTest }
      inputDataTest={ inputDataTest }
    />
  </>
);

const RoadshowSharedPresentationField: FC<IProps> = ({ isCallExpired = false }) => {
  const { instance } = useCallFormContext();
  const form = useForm<TRoadshowSharedPresentationFormValues>();
  const { values } = form.getState();

  const disabledDatePicker = isCallExpired || values.roadshowSharedPresentation !== 'Yes';
  const selectedTimeZone = values.timeZoneId?.timeZone;

  return (
    <Field
      name={ QUESTIONS_OACC.ROADSHOW_SHARED_PRESENTATION.name }
      render={ (fieldProps) => (
        <RadioGroup
          { ...fieldProps.input }
          disabled={ isCallExpired }
          onChange={ (event) => {
            fieldProps.input.onChange(event);
          } }
          direction='vertical'
          isErrorMessage={ FormStateSpecification.error(fieldProps.meta) }
          selectedValue={ fieldProps.input.value }
          dataTest={ QUESTIONS_OACC.ROADSHOW_SHARED_PRESENTATION.name }
          { ...fieldProps }
        >
          {
            QUESTIONS_OACC.ROADSHOW_SHARED_PRESENTATION.radioButtonAnswers.map((answer, index) => {
              return (
                <div key={ answer.value } className={ questionFieldsStyles.radioButtonAnswer }>
                  <Radio
                    dataTest={ answer.dataTest }
                    value={ answer.value }
                    label={ (
                      <>
                        { getQuestionTextValue(answer.label, instance) }
                        { answer.explanation && (
                          <Tooltip
                            containerClassName={ questionFieldsStyles.explanationIconContainer }
                            content={ getQuestionTextValue(answer.explanation, instance) }
                          >
                            <IconQuestion className={ questionFieldsStyles.explanationIcon } />
                          </Tooltip>
                        ) }
                      </>
                    ) }
                  />
                  { index === 0 && (
                    <div className={ styles.datePickerWrapper }>
                      <div className={ styles.datePickerTimeInput }>
                        <Field
                          htmlFor="roadshowSharedPresentationActivationTime"
                          name="roadshowSharedPresentationActivationTime"
                          component={ RoadshowDateTimePicker }
                          disabled={ disabledDatePicker }
                          timeZone={ selectedTimeZone }
                          label="Roadshow activation time"
                          placeholderText="Enter Roadshow Activation Date and Time"
                          dataTest="evercallScheduleRoadshowActivationTimePicker"
                          inputDataTest="addOnsFormRoadshowActivationTimeInput"
                        />
                      </div>
                      <div className={ styles.datePickerTimeInput }>
                        <Field
                          htmlFor="roadshowSharedPresentationDeactivationTime"
                          name="roadshowSharedPresentationDeactivationTime"
                          component={ RoadshowDateTimePicker }
                          disabled={ disabledDatePicker }
                          timeZone={ selectedTimeZone }
                          label="Roadshow deactivation time"
                          placeholderText="Enter Roadshow Deactivation Date and Time"
                          dataTest="evercallScheduleRoadshowDeactivationTimePicker"
                          inputDataTest="addOnsFormRoadshowDeactivationTimeInput"
                          { ...(
                            values.roadshowSharedPresentationActivationTime ? {
                              minDate: getDateFromUnixTime(values.roadshowSharedPresentationActivationTime),
                              minTime: getDateFromUnixTime(values.roadshowSharedPresentationActivationTime),
                            } : {}
                          ) }
                        />
                      </div>
                    </div>
                  ) }
                </div>
              );
            })
          }
        </RadioGroup>
      ) }
    />
  );
};

export default RoadshowSharedPresentationField;
